
import { defineComponent, ref, computed, watch, h, PropType } from 'vue'
import { useRoute } from 'vue-router'
import quantApi from '@/api/quantApi'
import StarNoData from '@/components/star/StarNoData.vue'
import moment from 'moment'
import { formatPctTrunc } from '@/utils/stock'

export default defineComponent({
  name: 'StockOpLog',
  props: {
    isDark: { type: Boolean, default: true },
    taskID: { type: String, default: '' },
    tradeID: { type: String, default: '' },
    symbol: { type: String, default: '' },
    name: { type: String, default: '' },
  },
  setup(props) {
    const route = useRoute()
    const stockSymbol = computed(() => props.symbol || '')
    const stockName = computed(() => props.name || '')

    const list = ref<any[]>([])
    const total = ref(0)
    const pageNo = ref(1)
    const pageSize = ref(20)
    const loading = ref(false)

    const tableLocale = computed(() => ({
      emptyText: () => h(StarNoData, { isDark: props.isDark })
    }))

    const oplogVars = computed(() => props.isDark ? {
      '--thead-bg': '#1f1f1f',
      '--thead-text': 'rgba(125,125,125,1)',
      '--row-text': 'rgba(255,255,255,1)',
      '--row-text-secondary': 'rgba(216,216,223,1)',
      '--row-hover-bg': 'rgba(61,61,61,0.5)',
      '--table-body-bg': '#141414',
      '--scrollbar-border': '#1e1e1e',
    } : {
      '--thead-bg': '#f5f5f5',
      '--thead-text': 'rgba(125,125,125,1)',
      '--row-text': '#1a1a1a',
      '--row-text-secondary': '#1a1a1a',
      '--row-hover-bg': '#f0f2f5',
      '--table-body-bg': '#ffffff',
      '--scrollbar-border': '#f5f5f5',
    })

    const fmtPrice = (val: number) => val != null ? Number(val).toFixed(2) : '--'
    const fmtDate = (ts: number) => ts ? moment(ts).format('YYYY-MM-DD') : '--'
    const secondary = (text: any) => h('span', { class: 'secondary-text' }, text ?? '--')

    const columns = computed(() => [
      { title: '序', customRender: ({ index }: any) => index + 1, width: 50, align: 'center' },
      { title: '代码', dataIndex: 'symbol', align: 'left', customRender: ({ text }: any) => secondary(stockSymbol.value || text) },
      { title: '名称', dataIndex: 'name', align: 'left', customRender: ({ text }: any) => secondary(stockName.value || text) },
      { title: '成本价', dataIndex: 'inPrice', align: 'right', customRender: ({ text }: any) => secondary(fmtPrice(text)) },
      { title: '卖出价', dataIndex: 'outPrice', align: 'right', customRender: ({ text }: any) => secondary(text != null ? fmtPrice(text) : '--') },
      { title: '收益率', dataIndex: 'profitRate', slots: { customRender: 'profitRate' }, align: 'right' },
      { title: '买入时间', dataIndex: 'inTime', align: 'right', customRender: ({ text }: any) => secondary(fmtDate(text)) },
      { title: '卖出时间', dataIndex: 'outTime', align: 'right', customRender: ({ text }: any) => secondary(text ? fmtDate(text) : '--') },
    ])

    const fetchList = async (append = false) => {
      if (!props.taskID || !props.tradeID) return
      loading.value = true
      try {
        const token = route.query.token as string
        const res = await quantApi.getStockOpLog({
          data: { taskID: props.taskID, tradeID: props.tradeID, pageNo: pageNo.value, pageSize: pageSize.value },
          headers: { token }
        })
        const newList = (res?.data?.list || []).map((item: any, i: number) => ({
          ...item,
          idx: (pageNo.value - 1) * pageSize.value + i
        }))
        list.value = append ? [...list.value, ...newList] : newList
        total.value = res?.data?.total || 0
      } catch (e) { /* 静默失败 */ }
      loading.value = false
    }

    const loadMore = () => {
      pageNo.value++
      fetchList(true)
    }

    watch(() => [props.taskID, props.tradeID], () => {
      pageNo.value = 1
      list.value = []
      fetchList()
    }, { immediate: true })

    return { list, total, pageNo, pageSize, loading, columns, tableLocale, oplogVars, loadMore, formatPctTrunc }
  }
})
